import { useNavigate } from "react-router";
import { useServices } from "@/app/use-services";
import { destinationAfterSignIn, PATHS } from "../destinations";
import { CodeEntry } from "../screens/CodeEntry";
import { EmailEntry } from "../screens/EmailEntry";
import { SignInChoice } from "../screens/SignInChoice";
import { useOnboardingShared } from "../use-onboarding-shared";
import { useAsyncAction } from "../use-async-action";

/** Where Google sends the person back to; the callback route finishes the job. */
function oauthRedirect(): string {
  return `${window.location.origin}${PATHS.authCallback}`;
}

export function SignInChoiceRoute() {
  const { session, analytics } = useServices();
  const navigate = useNavigate();
  const google = useAsyncAction("That sign-in didn’t work.");

  async function withGoogle() {
    analytics.track({ name: "sign-in chosen", method: "google" });
    // On success the browser is already on its way to Google.
    const ok = await google.run(() => session.signInWithGoogle(oauthRedirect()));
    if (!ok) analytics.track({ name: "sign-in failed", method: "google", step: "start" });
  }

  return (
    <SignInChoice
      onGoogle={() => void withGoogle()}
      onEmail={() => {
        analytics.track({ name: "sign-in chosen", method: "email" });
        navigate(PATHS.email);
      }}
      error={google.error}
      busy={google.busy}
    />
  );
}

/** Sends the code. A send that failed must never look like one that worked. */
export function EmailRoute() {
  const { session, analytics } = useServices();
  const navigate = useNavigate();
  const shared = useOnboardingShared();
  const send = useAsyncAction("We couldn’t send the code.");

  async function submit(email: string) {
    const ok = await send.run(() => session.sendEmailCode(email));
    analytics.track(ok ? { name: "email code sent" } : { name: "sign-in failed", method: "email", step: "send_code" });
    if (ok) {
      shared.setEmail(email);
      navigate(PATHS.code);
    }
  }

  return <EmailEntry initialEmail={shared.email} onSubmit={submit} error={send.error} busy={send.busy} />;
}

/** Verifies the code and lands exactly where the Google route lands. */
export function CodeRoute() {
  const { session, draft, analytics } = useServices();
  const navigate = useNavigate();
  const shared = useOnboardingShared();
  const verify = useAsyncAction("That code didn’t work.");
  const resend = useAsyncAction("We couldn’t send the code again.");

  if (!shared.email) {
    navigate(PATHS.email, { replace: true });
    return null;
  }

  async function submit(code: string) {
    const ok = await verify.run(() => session.verifyEmailCode(shared.email, code));
    analytics.track(ok ? { name: "signed in", method: "email" } : { name: "sign-in failed", method: "email", step: "verify_code" });
    if (ok) navigate(destinationAfterSignIn(draft.getSnapshot()), { replace: true });
  }

  async function resendCode() {
    const ok = await resend.run(() => session.sendEmailCode(shared.email));
    analytics.track(ok ? { name: "email code resent" } : { name: "sign-in failed", method: "email", step: "send_code" });
  }

  return (
    <CodeEntry
      destination={shared.email}
      onSubmit={submit}
      onResend={() => void resendCode()}
      error={verify.error ?? resend.error}
      busy={verify.busy || resend.busy}
    />
  );
}
